import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Target, Eye, EyeOff, ArrowRight, Shield, Zap, TrendingUp, Check, Gift } from 'lucide-react';
import api from '../lib/api';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, error } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      // Track the click
      api.post('/referral/track-click', { code: ref }).catch(() => {});
      // Validate the code
      api.get(`/referral/validate/${ref}`).then(res => {
        setReferralValid(res.data.valid);
      }).catch(() => setReferralValid(false));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    if (!legalAccepted) {
      setValidationError('You must agree to the Terms of Use & Risk Disclosure to continue');
      return;
    }
    
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    const result = await register(email, password, referralCode || undefined);
    
    if (result.success) {
      navigate('/');
    }
    
    setIsLoading(false);
  };

  const features = [
    { icon: TrendingUp, text: 'Copy winning traders automatically' },
    { icon: Shield, text: 'Bank-grade encryption for your keys' },
    { icon: Zap, text: 'Real-time trade execution' },
  ];

  return (
    <div className="min-h-screen bg-dark-900 flex items-start sm:items-center justify-center px-4 py-10 sm:p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-accent-600/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
      </div>
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center mb-6 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl rotate-6 opacity-60 blur-md scale-110" />
            <div className="relative w-20 h-20 bg-dark-900/20 rounded-2xl flex items-center justify-center shadow-2xl shadow-primary-500/30 overflow-hidden">
              <img
                src="/BETMIMICLOGO.jpg"
                alt="BetMimic"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-2">
            <span className="text-gradient">BetMimic</span>
          </h1>
          <p className="text-dark-400 text-lg">Start copy trading in minutes</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="card card-glow space-y-5 animate-slide-up">
          <div className="text-center pb-2">
            <h2 className="text-xl font-semibold">Create your account</h2>
            <p className="text-dark-500 text-sm mt-1">Free to start • No credit card required</p>
          </div>

          {referralCode && referralValid && (
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-200 text-sm flex items-center gap-3">
              <Gift className="w-5 h-5 text-green-400 flex-shrink-0" />
              <div>
                <div className="font-semibold">You were referred!</div>
                <div className="text-green-200/80 text-xs mt-0.5">
                  You're signing up with a referral link
                </div>
              </div>
            </div>
          )}

          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-200 text-sm">
            <div className="font-semibold">High-risk trading warning</div>
            <div className="mt-1 text-red-200/90">
              Prediction markets and automation can lead to rapid losses. BetMimic is not a broker, exchange, custodian,
              or investment adviser. You are solely responsible for your trades.
            </div>
            <div className="mt-3 flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={legalAccepted}
                onChange={(e) => setLegalAccepted(e.target.checked)}
              />
              <div className="text-sm">
                I agree to the{' '}
                <Link to="/legal" className="text-primary-300 hover:text-primary-200 underline">
                  Terms of Use & Risk Disclosure
                </Link>
                .
              </div>
            </div>
          </div>
          
          {(error || validationError) && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              {error || validationError}
            </div>
          )}

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label className="label">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input pr-12"
                placeholder="Min. 8 characters"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-500 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="label">Confirm Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-gradient w-full py-3.5 text-base group"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating account...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                Get Started Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            )}
          </button>
        </form>

        <p className="text-center text-dark-500 mt-6 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          Already have an account?{' '}
          <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium">
            Sign in
          </Link>
        </p>
        
        {/* Features */}
        <div className="mt-8 space-y-3 animate-fade-in" style={{ animationDelay: '0.5s' }}>
          {features.map((feature, i) => (
            <div key={i} className="flex items-center gap-3 text-dark-400 text-sm">
              <div className="w-8 h-8 rounded-lg bg-dark-800/60 flex items-center justify-center">
                <feature.icon className="w-4 h-4 text-primary-500" />
              </div>
              <span>{feature.text}</span>
              <Check className="w-4 h-4 text-emerald-500 ml-auto" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
